/**
 * Transaction API Service
 *
 * Renderer-side wrapper for transaction IPC calls.
 * Provides typed functions for begin/commit/rollback/release.
 */

import { STORAGE_ENGINE_UNSUPPORTED } from '@/core/engines/azureStorageRefusal';
import type { TransactionIsolationLevel } from '../../core/types/workspaceTypes';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';

export type TransactionEngineType = 'mssql' | 'pgsql' | 'mysql' | 'sqlite' | 'oracle' | 'mock';

export type TransactionResult = {
  success: boolean;
  error?: string;
  /** Machine-readable refusal (`SESSION_ACQUIRE_BLOCKED`, `SETTLE_IN_PROGRESS`); absent for engine errors. */
  code?: string;
};

/**
 * `code` of a Commit/Rollback refused because the OTHER one is still settling
 * the tab's session (mirrors `TRANSACTION_SETTLE_IN_PROGRESS_CODE` in
 * `electron/transaction/transactionEngineSession.ts`). The backend never runs
 * the second op on top of the first; the toolbar disables both while one is in
 * flight, and this is the backstop for a shortcut that slipped through.
 */
export const TRANSACTION_SETTLE_IN_PROGRESS_CODE = 'SETTLE_IN_PROGRESS';

/** `beginTransaction` result — `sessionGeneration` identifies the session the tab now holds. */
export type TransactionBeginResult = TransactionResult & { sessionGeneration?: number };

/** What a transaction's end could be CONFIRMED as (mirrors `TransactionSessionReleaseOutcome`). */
export type TransactionReleaseOutcome = 'rolled_back' | 'committed' | 'unknown';

/** `releaseTransaction` result — `outcome` is absent when the backend held no session for the tab. */
export type TransactionReleaseResult = TransactionResult & { outcome?: TransactionReleaseOutcome };

/** Map DatabaseEngineId to TransactionEngineType. Kusto has no transaction support (KQL is not transactional). */
export function toTransactionEngineType(engineId: DatabaseEngineId): TransactionEngineType {
  if (engineId === 'kusto') {
    throw new Error('Transactions are not supported for the Azure Data Explorer (kusto) engine');
  }
  if (engineId === 'azurestorage') {
    throw Object.assign(new Error(STORAGE_ENGINE_UNSUPPORTED.message), { code: STORAGE_ENGINE_UNSUPPORTED.code });
  }
  return engineId;
}

const UNAVAILABLE: TransactionResult = { success: false, error: 'Transaction API is unavailable' };

export async function beginTransaction(
  tabId: string,
  connectionId: string,
  database: string,
  engineType: TransactionEngineType,
  isolationLevel?: TransactionIsolationLevel,
  /** Names this attempt so `abandonTransactionBegin` can give up on it. */
  attemptId?: string,
): Promise<TransactionBeginResult> {
  if (!window.electronAPI?.transaction?.begin) return UNAVAILABLE;
  try {
    return await window.electronAPI.transaction.begin({ tabId, connectionId, database, engineType, isolationLevel, attemptId });
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Give up on a BEGIN still on the wire: main rolls its session back and
 * releases it in the background when it arrives (or right away if it already
 * did). Fire-and-forget — never awaited by the UI.
 */
export function abandonTransactionBegin(tabId: string, attemptId: string): void {
  void window.electronAPI?.transaction?.abandonBegin?.({ tabId, attemptId }).catch(() => undefined);
}

export async function commitTransaction(tabId: string): Promise<TransactionResult> {
  if (!window.electronAPI?.transaction?.commit) return UNAVAILABLE;
  try {
    return await window.electronAPI.transaction.commit({ tabId });
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

export async function rollbackTransaction(tabId: string): Promise<TransactionResult> {
  if (!window.electronAPI?.transaction?.rollback) return UNAVAILABLE;
  try {
    return await window.electronAPI.transaction.rollback({ tabId });
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

export async function releaseTransaction(tabId: string): Promise<TransactionReleaseResult> {
  if (!window.electronAPI?.transaction?.release) return UNAVAILABLE;
  try {
    return await window.electronAPI.transaction.release({ tabId });
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}
